import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { DialogFooter } from "@/components/ui/dialog";
import Spinner from "@/components/spinner";

import { useState } from "react";

const phoneRegex = new RegExp(
  /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/
);
const FormSchema = z.object({
  name: z.string().min(2, {
    message: "Name must be at least 2 characters.",
  }),
  email: z.string().email({
    message: "Please provide a valid email address.",
  }),
  phone: z
    .string()
    .regex(phoneRegex, "Invalid Phone Number")
    .min(10, { message: "Must be a valid mobile number" })
    .max(14, { message: "Must be a valid mobile number" }),

  resume: z.any().refine((value) => !!value, {
    message: "Please add a resume",
  }),
});

export function CareerApplicationForm({
  data,
  setOpen,
}: {
  data: any;
  // eslint-disable-next-line no-unused-vars
  setOpen: (value: boolean) => void;
}) {
  const [loading, setLoading] = useState(false);
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
    },
  });

  async function onSubmit(values: z.infer<typeof FormSchema>) {
    try {
      setLoading(true);
      const base64Resume = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(values.resume);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
      });
      const uploadFileResponse = await fetch("/api/submit-job-application", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...values,
          resume: base64Resume,
          application: data,
        }),
        cache: "no-cache",
      });

      if (!uploadFileResponse.ok) {
        throw new Error("Error submitting application");
      }
      await uploadFileResponse.json();
      toast({
        title: "Your application has been submitted.",
        description: `A confirmation email has been sent to ${values.email}.`,
      });
      setLoading(false);
      setOpen(false);
    } catch (error) {
      setLoading(false);
      toast({
        title: "There was an error submitting your application.",
        description: "Please try again later.",
        variant: "destructive",
      });
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  autoComplete="email"
                  type="email"
                  placeholder="Your Email"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="Your Phone Number" {...field} />
              </FormControl>
              <FormDescription>
                Enter your 10 digit mobile number so we can contact you.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="resume"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Resume</FormLabel>
              <FormControl>
                <Input
                  accept=".pdf"
                  type="file"
                  // {...field}
                  onChange={(e) => {
                    field.onChange(e.target.files ? e.target.files[0] : null);
                  }}
                />
              </FormControl>
              <FormDescription>Upload a PDF of your resume.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {loading ? (
          <div className="z-50 bg-white/80 inset-0 [&>*]:!m-0 absolute flex h-full w-full !m-0 flex-col items-center justify-center">
            <Spinner />
          </div>
        ) : null}

        <DialogFooter>
          <Button disabled={loading} type="submit">
            Submit
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
